import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  Button,
  Stack,
  Paper,
  Avatar,
  Chip,
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@mui/lab';
import {
  ArrowBack,
  Business,
  TrendingUp,
  EmojiEvents,
  Group,
  Lightbulb,
  Factory,
  Rocket,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

export const AboutStory: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const milestones = [
    {
      year: '2010',
      title: 'Company Founded',
      description: 'Started as a small manufacturing workshop with a vision to revolutionize industrial production in East Africa.',
      icon: <Business />,
      color: 'primary',
    },
    {
      year: '2013',
      title: 'First Major Contract',
      description: 'Secured our first major manufacturing contract, marking the beginning of our growth journey.',
      icon: <TrendingUp />,
      color: 'success',
    },
    {
      year: '2016',
      title: 'Technology Integration',
      description: 'Invested in advanced automation and IoT technologies, positioning ourselves as a modern manufacturing leader.',
      icon: <Lightbulb />,
      color: 'warning',
    },
    {
      year: '2019',
      title: 'Regional Expansion',
      description: 'Expanded operations across East Africa, establishing partnerships with leading companies in the region.',
      icon: <Factory />,
      color: 'info',
    },
    {
      year: '2022',
      title: 'Innovation Hub Launch',
      description: 'Launched our innovation hub, focusing on sustainable manufacturing and green technologies.',
      icon: <Rocket />,
      color: 'secondary',
    },
    {
      year: '2024',
      title: 'Industry Leader',
      description: 'Recognized as a leading manufacturing company in East Africa, with cutting-edge facilities and sustainable practices.',
      icon: <EmojiEvents />,
      color: 'primary',
    },
  ];

  const values = [
    {
      title: 'Innovation',
      description: 'Constantly pushing boundaries and embracing new technologies to deliver cutting-edge solutions.',
      icon: <Lightbulb />,
    },
    {
      title: 'Quality',
      description: 'Uncompromising commitment to excellence in every product and service we deliver.',
      icon: <EmojiEvents />,
    },
    {
      title: 'Sustainability',
      description: 'Dedicated to environmentally responsible manufacturing practices and green technologies.',
      icon: <TrendingUp />,
    },
    {
      title: 'Collaboration',
      description: 'Building strong partnerships with clients, suppliers, and communities for mutual success.',
      icon: <Group />,
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 4,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: 3,
            border: '1px solid rgba(255, 255, 255, 0.2)',
          }}
        >
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/about')}
            sx={{ mb: 3 }}
          >
            Back to About
          </Button>
          
          <Typography variant="h2" gutterBottom sx={{ fontWeight: 700 }}>
            Our Story
          </Typography>
          <Typography variant="h5" color="text.secondary" sx={{ mb: 3 }}>
            From humble beginnings to industry leadership - the journey of Monio Production
          </Typography>
          
          <Typography variant="body1" sx={{ lineHeight: 1.8, fontSize: '1.1rem' }}>
            Founded in 2010, Monio Production began as a small manufacturing workshop with a big vision: to revolutionize 
            industrial production in East Africa. What started with just a handful of dedicated employees has grown into 
            one of the region's most innovative and sustainable manufacturing companies.
          </Typography>
        </Paper>

        {/* Mission & Vision */}
        <Grid container spacing={4} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                height: '100%',
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                borderRadius: 3,
                border: '1px solid rgba(255, 255, 255, 0.2)',
              }}
            >
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
                Our Mission
              </Typography>
              <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 3 }}>
                To deliver innovative, high-quality manufacturing solutions that drive economic growth and sustainability 
                in East Africa while empowering our communities and protecting our environment.
              </Typography>
              <Chip label="Innovation" color="primary" sx={{ mr: 1, mb: 1 }} />
              <Chip label="Quality" color="success" sx={{ mr: 1, mb: 1 }} />
              <Chip label="Sustainability" color="warning" />
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                height: '100%',
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                borderRadius: 3,
                border: '1px solid rgba(255, 255, 255, 0.2)',
              }}
            >
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
                Our Vision
              </Typography>
              <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 3 }}>
                To be the leading manufacturing company in East Africa, known for innovation, sustainability, and 
                excellence, while creating lasting value for our stakeholders and contributing to regional development.
              </Typography>
              <Chip label="Leadership" color="primary" sx={{ mr: 1, mb: 1 }} />
              <Chip label="Excellence" color="success" sx={{ mr: 1, mb: 1 }} />
              <Chip label="Impact" color="secondary" />
            </Paper>
          </Grid>
        </Grid>

        {/* Company Timeline */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 4,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: 3,
            border: '1px solid rgba(255, 255, 255, 0.2)',
          }}
        >
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, textAlign: 'center', mb: 4 }}>
            Our Journey
          </Typography>
          
          <Timeline position="alternate">
            {milestones.map((milestone, index) => (
              <TimelineItem key={index}>
                <TimelineOppositeContent sx={{ m: 'auto 0' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {milestone.year}
                  </Typography>
                </TimelineOppositeContent>
                <TimelineSeparator>
                  <TimelineDot color={milestone.color as any}>
                    {milestone.icon}
                  </TimelineDot>
                  {index < milestones.length - 1 && <TimelineConnector />}
                </TimelineSeparator>
                <TimelineContent sx={{ py: '12px', px: 2 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      background: 'rgba(99, 102, 241, 0.05)',
                      border: '1px solid rgba(99, 102, 241, 0.1)',
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
                      {milestone.title}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {milestone.description}
                    </Typography>
                  </Paper>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        </Paper>

        {/* Core Values */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 4,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: 3,
            border: '1px solid rgba(255, 255, 255, 0.2)',
          }}
        >
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, textAlign: 'center', mb: 4 }}>
            Our Core Values
          </Typography>
          
          <Grid container spacing={3}>
            {values.map((value, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.95) 100%)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 3,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0px 20px 40px rgba(0, 0, 0, 0.1)',
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 60,
                      height: 60,
                      mx: 'auto',
                      mb: 2,
                      background: 'linear-gradient(135deg, #1b4d89 0%, #0f3460 100%)',
                    }}
                  >
                    {value.icon}
                  </Avatar>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    {value.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {value.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Call to Action */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: 'center',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: 3,
            border: '1px solid rgba(255, 255, 255, 0.2)',
          }}
        >
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            Join Our Journey
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Be part of our continued growth and innovation. Whether you're a client, partner, or team member, 
            we invite you to contribute to our story.
          </Typography>
          
          <Stack direction="row" spacing={3} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/contact')}
              sx={{
                px: 4,
                py: 1.5,
                background: 'linear-gradient(135deg, #1b4d89 0%, #0f3460 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0f3460 0%, #1b4d89 100%)',
                },
              }}
            >
              Get in Touch
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/careers')}
              sx={{
                px: 4,
                py: 1.5,
                border: '2px solid #6366f1',
                color: '#6366f1',
                '&:hover': {
                  border: '2px solid #4f46e5',
                  background: 'rgba(99, 102, 241, 0.04)',
                },
              }}
            >
              Join Our Team
            </Button>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}; 